import { apiClient } from './client';
import type { Contract, ContractDetail, ContractServiceLink, ContractWrite } from '@/types/oscar';

export interface ContractsQuery {
  type?: string;
  status?: string;
  expiringDays?: number;
}

function qs(q: ContractsQuery): string {
  const p = new URLSearchParams();
  if (q.type) p.set('type', q.type);
  if (q.status) p.set('status', q.status);
  if (q.expiringDays) p.set('expiringDays', String(q.expiringDays));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function listContracts(query: ContractsQuery = {}, signal?: AbortSignal): Promise<Contract[]> {
  return apiClient.get<Contract[]>(`/api/contracts${qs(query)}`, signal);
}
export function getContract(id: number, signal?: AbortSignal): Promise<ContractDetail> {
  return apiClient.get<ContractDetail>(`/api/contracts/${id}`, signal);
}
export function createContract(body: ContractWrite): Promise<Contract> {
  return apiClient.post<Contract>('/api/contracts', body);
}
export function updateContract(id: number, body: ContractWrite): Promise<Contract> {
  return apiClient.patch<Contract>(`/api/contracts/${id}`, body);
}
/** Replace the services this contract funds (CMDB cost showback). Returns the new set. */
export function setContractServices(id: number, serviceIds: number[]): Promise<ContractServiceLink[]> {
  return apiClient.post<ContractServiceLink[]>(`/api/contracts/${id}/services`, { ServiceIds: serviceIds });
}
export interface LineItemBody {
  LineItemKind: 'Asset' | 'SoftwarePool' | 'SimPlan';
  AssetId?: number;
  SoftwarePoolId?: number;
  SimPlanId?: number;
  Description?: string;
  MonthlyCost?: number;
  VendorRef?: string;
  /** SoftwarePool lines only — contracted seats and unit price. */
  TotalSeats?: number;
  CostPerSeat?: number;
  /** SoftwarePool bundle: the pools this line entitles (one or more) — now optional. */
  SoftwarePoolIds?: number[];
  /** GL account to code this line to; omitted → the kind's configured default. */
  GlAccountId?: number;
}
export function addContractLineItem(id: number, body: LineItemBody): Promise<{ LineItemId: number }> {
  return apiClient.post<{ LineItemId: number }>(`/api/contracts/${id}/line-items`, body);
}
/** Invoice-matching fields on a line item — vendor service/billing number, description, monthly cost. */
export interface LineItemPatch {
  VendorRef?: string | null;
  Description?: string | null;
  MonthlyCost?: number | null;
  TotalSeats?: number | null;
  CostPerSeat?: number | null;
  /** When present, replaces the full set of pools a SoftwarePool line entitles (empty clears). */
  SoftwarePoolIds?: number[];
  /** GL account to code this line to; 0 clears it. */
  GlAccountId?: number | null;
}
export function updateContractLineItem(id: number, lineItemId: number, body: LineItemPatch): Promise<{ LineItemId: number }> {
  return apiClient.patch<{ LineItemId: number }>(`/api/contracts/${id}/line-items/${lineItemId}`, body);
}

// ── GL accounts (migration 046) ─────────────────────────────────────────────

export interface GlAccount { GlAccountId: number; AccountCode: string; AccountDescription: string }
export interface LineItemGlDefault { LineItemKind: string; GlAccountId: number; AccountCode: string | null; AccountDescription: string | null }

/** Search expense GL accounts (oscar.vGlAccount) by code / description. */
export function searchGlAccounts(search: string, signal?: AbortSignal): Promise<GlAccount[]> {
  const qs = search ? `?search=${encodeURIComponent(search)}` : '';
  return apiClient.get<GlAccount[]>(`/api/gl-accounts${qs}`, signal);
}
/** Default GL account per line-item kind (config page + pre-fill). */
export function getLineItemGlDefaults(signal?: AbortSignal): Promise<LineItemGlDefault[]> {
  return apiClient.get<LineItemGlDefault[]>('/api/contracts/line-item-gl-defaults', signal);
}
/** Set a line-item kind's default GL account (admin). */
export function setLineItemGlDefault(kind: string, glAccountId: number): Promise<LineItemGlDefault> {
  return apiClient.put<LineItemGlDefault>(`/api/contracts/line-item-gl-defaults/${encodeURIComponent(kind)}`, { GlAccountId: glAccountId });
}
